import { Suspense } from "react";
import { Switch, Route, Redirect, Router as WouterRouter } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/auth-provider";
import { Layout } from "@/components/layout";
import { PRO_OFFERING_ENABLED } from "@/lib/proOffering";
import { lazyPage } from "@/lib/staleChunk";

const Landing = lazyPage(() => import("@/pages/landing"));
const Browse = lazyPage(() => import("@/pages/browse"));
const ListingDetail = lazyPage(() => import("@/pages/listing-detail"));
const PostListing = lazyPage(() => import("@/pages/post-listing"));
const MyListings = lazyPage(() => import("@/pages/my-listings"));
const Messages = lazyPage(() => import("@/pages/messages"));
const WantedBoard = lazyPage(() => import("@/pages/wanted-board"));
const WantedNew = lazyPage(() => import("@/pages/wanted-new"));
const WantedDetail = lazyPage(() => import("@/pages/wanted-detail"));
const SellerProfile = lazyPage(() => import("@/pages/seller-profile"));
const SavedListings = lazyPage(() => import("@/pages/saved-listings"));
const CheckoutSuccess = lazyPage(() => import("@/pages/checkout-success"));
const CheckoutCancel = lazyPage(() => import("@/pages/checkout-cancel"));
const Privacy = lazyPage(() => import("@/pages/privacy"));
const HowItWorks = lazyPage(() => import("@/pages/how-it-works"));
const FeedbackPage = lazyPage(() => import("@/pages/feedback"));
const AdminInsights = lazyPage(() => import("@/pages/admin-insights"));
const Terms = lazyPage(() => import("@/pages/terms"));
const AdminDashboard = lazyPage(() => import("@/pages/admin-dashboard"));
const AdminLedger = lazyPage(() => import("@/pages/admin-ledger"));
const AdminOps = lazyPage(() => import("@/pages/admin-ops"));
const ResetPassword = lazyPage(() => import("@/pages/reset-password"));
const FAQ = lazyPage(() => import("@/pages/faq"));
const Support = lazyPage(() => import("@/pages/support"));
const Safety = lazyPage(() => import("@/pages/safety"));
const Download = lazyPage(() => import("@/pages/download"));
const About = lazyPage(() => import("@/pages/about"));
const DeleteAccount = lazyPage(() => import("@/pages/delete-account"));
const AdminFinances = lazyPage(() => import("@/pages/admin-finances"));
const AdminAlphaKpi = lazyPage(() => import("@/pages/admin-alpha-kpi"));
const ListingSuccess = lazyPage(() => import("@/pages/listing-success"));
const ProDashboard = lazyPage(() => import("@/pages/pro-dashboard"));
const OrderBridge = lazyPage(() => import("@/pages/order-bridge"));
const NotFound = lazyPage(() => import("@/pages/not-found"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

const PageFallback = () => (
  <div
    style={{
      minHeight: "60vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  />
);

function Router() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Switch>
        {/* Deep-link bridge for mobile order links — intentionally minimal
            chrome, not part of normal site navigation. */}
        <Route path="/open/order/:reservationId" component={OrderBridge} />
        {/* Account deletion info page — publicly accessible, no app nav */}
        <Route path="/delete-account" component={DeleteAccount} />
        {/* Admin Ops — standalone dark dashboard */}
        <Route path="/admin" component={AdminOps} />

        {/* Every other page — marketing pages included — uses the same
            app Layout header/footer, so navigation looks identical whether
            or not you're signed in. Layout sits OUTSIDE its own Suspense
            boundary (nested below, wrapping only the inner Switch) so
            header/nav stay mounted across in-app navigations instead of
            unmounting into the outer fallback every time a not-yet-loaded
            lazy chunk suspends. */}
        <Route>
          <Layout>
            <Suspense fallback={<PageFallback />}>
              <Switch>
                <Route path="/" component={Landing} />
                {/* The old marketplace homepage, replaced by the landing page before launch. */}
                <Route path="/home"><Redirect to="/" replace /></Route>
                <Route path="/how-it-works" component={HowItWorks} />
                <Route path="/about" component={About} />
                <Route path="/browse" component={Browse} />
                <Route path="/listings/:id" component={ListingDetail} />
                <Route path="/post-listing" component={PostListing} />
                <Route path="/my-listings" component={MyListings} />
                <Route path="/messages" component={Messages} />
                <Route path="/wanted" component={WantedBoard} />
                <Route path="/wanted/new" component={WantedNew} />
                <Route path="/wanted/:id" component={WantedDetail} />
                <Route path="/sellers/:id" component={SellerProfile} />
                <Route path="/saved" component={SavedListings} />
                <Route path="/checkout/success" component={CheckoutSuccess} />
                <Route path="/checkout/cancel" component={CheckoutCancel} />
                <Route path="/privacy" component={Privacy} />
                {/* Round 34: the community page was invented content (posts, authors, statistics) and is gone. */}
                <Route path="/community"><Redirect to="/" replace /></Route>
                <Route path="/feedback" component={FeedbackPage} />
                <Route path="/admin/insights" component={AdminInsights} />
                <Route path="/admin/dashboard" component={AdminDashboard} />
                <Route path="/admin/ledger" component={AdminLedger} />
                <Route path="/admin/finances" component={AdminFinances} />
                <Route path="/admin/alpha-kpi" component={AdminAlphaKpi} />
                <Route path="/terms" component={Terms} />
                <Route path="/reset-password" component={ResetPassword} />
                <Route path="/faq" component={FAQ} />
                <Route path="/support" component={Support} />
                <Route path="/safety" component={Safety} />
                <Route path="/download" component={Download} />
                <Route path="/listing-success" component={ListingSuccess} />
                {/* Dormant since 2026-10-18 (Pro withdrawn): PRO_OFFERING_ENABLED is false. */}
                {PRO_OFFERING_ENABLED && (
                  <Route path="/pro-dashboard" component={ProDashboard} />
                )}
                <Route component={NotFound} />
              </Switch>
            </Suspense>
          </Layout>
        </Route>
      </Switch>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider>
            <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
              <Router />
            </WouterRouter>
            <Toaster />
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
